import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Package, Plus, AlertTriangle, Minus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const CATEGORIES = ['produce', 'dairy', 'meat', 'grains', 'canned', 'frozen', 'beverage', 'condiment', 'supply', 'other'];
const UNITS = ['each', 'lb', 'oz', 'gallon', 'quart', 'case', 'box', 'bag', 'can', 'dozen'];

const emptyForm = { name: '', category: 'other', unit: 'each', quantity_on_hand: 0, unit_cost: 0, reorder_level: 0, supplier: '', notes: '' };

const InventoryManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory-items`);
      setItems(r.data);
    } catch (e) { toast.error('Failed to load inventory'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setShowDialog(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const payload = {
      ...form,
      quantity_on_hand: parseFloat(form.quantity_on_hand) || 0,
      unit_cost: parseFloat(form.unit_cost) || 0,
      reorder_level: parseFloat(form.reorder_level) || 0,
    };
    try {
      if (editing) await axios.put(`${API}/inventory-items/${editing.id}`, payload);
      else await axios.post(`${API}/inventory-items`, payload);
      toast.success(editing ? 'Item updated' : 'Item added');
      setShowDialog(false); load();
    } catch (e) { toast.error('Could not save item'); }
  };

  const adjust = async (item, delta) => {
    try { await axios.post(`${API}/inventory-items/${item.id}/adjust`, { delta }); load(); }
    catch (e) { toast.error('Adjust failed'); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try { await axios.delete(`${API}/inventory-items/${item.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const lowStock = items.filter((i) => i.quantity_on_hand <= i.reorder_level && i.reorder_level > 0);
  const totalValue = items.reduce((s, i) => s + (i.quantity_on_hand * i.unit_cost), 0);
  const shown = filter === 'all' ? items : filter === 'low' ? lowStock : items.filter((i) => i.category === filter);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Package className="h-6 w-6 text-indigo-600" /> Food Inventory & Supplies</h1>
          <p className="text-sm text-gray-600 mt-1">Track stock, costs and low-stock alerts. Connected to meal planning.</p>
        </div>
        <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-inventory-btn"><Plus className="h-4 w-4 mr-1" /> Add item</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 card-shadow"><CardContent className="p-4"><div className="text-2xl font-bold">{items.length}</div><div className="text-sm text-gray-500">Items tracked</div></CardContent></Card>
        <Card className="border-0 card-shadow"><CardContent className="p-4"><div className="text-2xl font-bold text-amber-600">{lowStock.length}</div><div className="text-sm text-gray-500">Low stock</div></CardContent></Card>
        <Card className="border-0 card-shadow"><CardContent className="p-4"><div className="text-2xl font-bold">${totalValue.toFixed(2)}</div><div className="text-sm text-gray-500">Inventory value</div></CardContent></Card>
        <Card className="border-0 card-shadow"><CardContent className="p-4"><div className="text-2xl font-bold">{new Set(items.map((i) => i.category)).size}</div><div className="text-sm text-gray-500">Categories in use</div></CardContent></Card>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>All</Button>
        <Button variant={filter === 'low' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('low')} data-testid="filter-low-stock">Low stock ({lowStock.length})</Button>
        {CATEGORIES.map((c) => <Button key={c} variant={filter === c ? 'default' : 'outline'} size="sm" className="capitalize" onClick={() => setFilter(c)}>{c}</Button>)}
      </div>

      <Card className="border-0 card-shadow">
        <CardContent className="overflow-x-auto p-0">
          {loading ? <p className="p-6 text-gray-500">Loading...</p> : shown.length === 0 ? <p className="p-6 text-gray-500" data-testid="no-inventory">No items.</p> : (
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="p-3">Item</th><th className="p-3">Category</th><th className="p-3">On hand</th>
                <th className="p-3">Unit cost</th><th className="p-3">Value</th><th className="p-3">Supplier</th><th className="p-3">Actions</th>
              </tr></thead>
              <tbody>
                {shown.map((i) => {
                  const low = i.quantity_on_hand <= i.reorder_level && i.reorder_level > 0;
                  return (
                    <tr key={i.id} className="border-b last:border-0" data-testid={`inventory-row-${i.id}`}>
                      <td className="p-3 font-medium text-gray-900">{i.name}{low && <Badge className="ml-2 bg-amber-100 text-amber-800"><AlertTriangle className="h-3 w-3 mr-1" />low</Badge>}</td>
                      <td className="p-3 capitalize">{i.category}</td>
                      <td className="p-3">{i.quantity_on_hand} {i.unit}</td>
                      <td className="p-3">${i.unit_cost.toFixed(2)}</td>
                      <td className="p-3">${(i.quantity_on_hand * i.unit_cost).toFixed(2)}</td>
                      <td className="p-3 text-gray-500">{i.supplier || '—'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => adjust(i, -1)} title="Consume 1" data-testid={`consume-${i.id}`}><Minus className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => adjust(i, 1)} title="Restock 1" data-testid={`restock-${i.id}`}><Plus className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(i)} title="Edit"><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(i)} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="inventory-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit item' : 'Add inventory item'}</DialogTitle>
            <DialogDescription>Track a food or supply item with cost and reorder level.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Item name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="inv-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="inv-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-gray-500">Quantity</label><Input type="number" value={form.quantity_on_hand} onChange={(e) => setForm({ ...form, quantity_on_hand: e.target.value })} data-testid="inv-qty" /></div>
              <div><label className="text-xs text-gray-500">Unit cost ($)</label><Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="inv-cost" /></div>
              <div><label className="text-xs text-gray-500">Reorder at</label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} data-testid="inv-reorder" /></div>
            </div>
            <Input placeholder="Supplier (optional)" value={form.supplier || ''} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="inv-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryManagement;
